/**
 * SystemModuleListBuilderPage — „Kreator Listy" (osobny widok, NIE modal).
 *
 * Krok UX 2026-06-01 (LIST_VIEW_BUILDER_PLAN — „Architektura paska + 3 kreatory").
 * Port struktury zakładek z modala SellCall „Ustawienia widoku listy", ale jako
 * pełny widok-route (deep-link, „wstecz", testowalność, spójność z Kreatorem
 * Formularza `SystemModuleBuilderPage`).
 *
 * Aktywne zakładki: Widgety + Kolumny + Szybkie filtry + Przyciski + Kanban +
 * Strona. Strona = pageSize + przełączniki paska nad listą; Przyciski = własne
 * etykiety akcji; Kanban = pole grupujące + pola kafelka + styl tablicy (zapis
 * do `list_config.kanbanConfig`). Zakładka Automatyzacje wyszarzona „wkrótce".
 *
 * Route: `/system-modules/:slug/kreator-listy[?layout=<id>]` i `/modules/:slug/kreator-listy[?layout=<id>]`.
 * `?layout=<id>` = edytowany widok listy (wybór w nagłówku — `ListViewSwitcher`, albo przekazany
 * z listy przez menu „Kreator ▾"); brak = widok domyślny. Standalone kreator każdej kartoteki tenanta.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ViewMode } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  Columns3,
  Filter,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  type LucideIcon,
  MousePointerClick,
  ScrollText,
  Zap,
} from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import {
  ListButtonsPanel,
  ListCalendarPanel,
  ListColumnsPanel,
  ListKanbanPanel,
  ListPagePanel,
  ListQuickFiltersPanel,
} from '@/components/configurable-table';
import { ListActionsPanel } from '@/components/configurable-table/ListActionsPanel';
import { ListWidgetsPanel } from '@/components/configurable-table/ListWidgetsPanel';
import {
  collectionEndpointFor,
  useTableLayout,
} from '@/components/configurable-table/useTableLayout';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { ListViewSwitcher } from '@/components/list-transfer/ListViewSwitcher';
import {
  isReadOnlyListView,
  useCreateListView,
} from '@/components/list-transfer/useCreateListView';
import { AutomationsPanel } from '@/components/modules/AutomationsPanel';
import { Spinner } from '@/components/Spinner';
import { useTabLabel } from '@/components/tabs/useTabLabel';
import {
  type ReferenceListAvailableField,
  ReferenceListContext,
  type ReferenceListContextValue,
  useReferenceListContext,
} from '@/contexts/ReferenceListContext';
import { api } from '@/lib/api';
import {
  isFieldListable,
  PAGE_OVERRIDE_REFERENCE_SLUGS,
  type SystemModuleFieldDef,
} from '@/lib/ext-list-columns';
import { resolveListKartoteka } from '@/lib/list-actions/list-kartoteka';
import { listSupportsViewMode } from '@/lib/list-view-support';
import { templateTableId, useModuleTemplateDetail } from '@/lib/module-templates/template-layouts';
import { mergeComputedProcessFields } from '@/lib/processes/computed-fields';

type ListBuilderTab =
  | 'widgets'
  | 'columns'
  | 'quickFilters'
  | 'buttons'
  | 'actions'
  | 'kanban'
  | 'calendar'
  | 'page'
  | 'automations';

interface TabDef {
  id: ListBuilderTab;
  labelKey: string;
  Icon: LucideIcon;
  /** Stan 2026-06-03: aktywne wszystkie poza `automations` (czeka na silnik automatyzacji). */
  enabled: boolean;
  /** Tryb widoku listy, który konfiguruje zakładka — gdy lista systemowa go nie obsługuje
   *  (`listSupportsViewMode`), zakładka jest w kreatorze ukryta. */
  viewMode?: ViewMode;
}

const TABS: TabDef[] = [
  { id: 'widgets', labelKey: 'tenant.listBuilder.tabs.widgets', Icon: LayoutGrid, enabled: true },
  { id: 'columns', labelKey: 'tenant.listBuilder.tabs.columns', Icon: Columns3, enabled: true },
  {
    id: 'quickFilters',
    labelKey: 'tenant.listBuilder.tabs.quickFilters',
    Icon: Filter,
    enabled: true,
  },
  {
    id: 'buttons',
    labelKey: 'tenant.listBuilder.tabs.buttons',
    Icon: MousePointerClick,
    enabled: true,
  },
  {
    id: 'actions',
    labelKey: 'tenant.listBuilder.tabs.actions',
    Icon: ListChecks,
    enabled: true,
  },
  {
    id: 'kanban',
    labelKey: 'tenant.listBuilder.tabs.kanban',
    Icon: LayoutDashboard,
    enabled: true,
    viewMode: 'kanban',
  },
  {
    id: 'calendar',
    labelKey: 'tenant.listBuilder.tabs.calendar',
    Icon: CalendarDays,
    enabled: true,
    viewMode: 'calendar',
  },
  { id: 'page', labelKey: 'tenant.listBuilder.tabs.page', Icon: ScrollText, enabled: true },
  {
    id: 'automations',
    labelKey: 'tenant.listBuilder.tabs.automations',
    Icon: Zap,
    enabled: false,
  },
];

export function SystemModuleListBuilderPage({
  scope = 'system',
  embeddedMode = false,
  code: codeProp,
  entitySlug: entitySlugProp,
  viewSlug: viewSlugProp,
  readOnlyMode = false,
  hideAddWidget = false,
  creatorKartoteka = false,
  forcedLayoutId,
}: {
  /** `system` (domyślne) — slug listy systemowej = tableId wprost.
   *  `module` — moduł własny: slug z trasy rozwiązujemy na `module-<id>`,
   *  bo persystencja layoutu jest kluczowana po id (jak runtime rekordów).
   *  `template` (hub Krok 2) — widok `list` SZABLONU w scope admin: trasa niesie
   *  `:slug/:entitySlug/:viewSlug` albo propsy (osadzony w ModuleTemplateEditor),
   *  tableId = `template:<code>:<entity>:<view>`
   *  (adapter persystencji w useTableLayout — PATCH configu widoku, zapis na żądanie). */
  scope?: 'system' | 'module' | 'template';
  /** Osadzenie w zakładce ustawień kartoteki — ukrywa własny nagłówek/„wstecz"
   *  i zewnętrzny padding (settings hub ma swój). Domyślnie standalone page. */
  embeddedMode?: boolean;
  /**
   * Opcjonalny kod szablonu (scope='template'). Gdy podany — używany zamiast `:slug`
   * z `useParams()`. Umożliwia osadzenie bez własnego routingu (prop-driven z fallbackiem).
   * Trasowe użycie (TemplateListBuilderPage) zostaje bez zmian — prop undefined → useParams.
   */
  code?: string;
  /**
   * Opcjonalny slug encji (scope='template'). Gdy podany — używany zamiast `:entitySlug`
   * z `useParams()`. Trasowe użycie zostaje bez zmian.
   */
  entitySlug?: string;
  /**
   * Opcjonalny slug widoku (scope='template'). Gdy podany — używany zamiast `:viewSlug`
   * z `useParams()`. Trasowe użycie zostaje bez zmian.
   */
  viewSlug?: string;
  /**
   * Gdy true: builder otwiera się w trybie podglądu (read-only).
   * Panele są nieedytowalne (pointer-events: none + atrybut inert usuwa kontrolki
   * z tab order). Przycisk Zapisz jest niedostępny: ReferenceListContext dostarcza
   * onListConfigSave=undefined, więc saveMutation nie ma interceptora do wywołania.
   * Domyślnie false — zachowanie bez zmian (edytowalny).
   */
  readOnlyMode?: boolean;
  /**
   * HIDDEN (tymczasowo): ukrywa TYLKO przycisk „+ Dodaj widget" (i otwierany przezeń
   * picker typu widgetu) w zakładce „Widgety". Sama zakładka oraz sekcja „Domyślny
   * wygląd kart" pozostają widoczne i edytowalne.
   * Powód: dodawanie widgetów w builderze widoku listy pozycji menu nie ma jeszcze sensu —
   * skonfigurowane tu widgety / nazwane widoki menu nie renderują się u klienta
   * (materializeEntityLayouts bierze tylko isDefault; render multi-view u klienta = TODO „krok D").
   * Odblokować, gdy render nazwanych widoków u tenanta będzie gotowy.
   * Domyślnie false — standalone builder + kreator encji-odwołania nie są dotknięte.
   */
  hideAddWidget?: boolean;
  /**
   * Biblioteka układów kartoteki z KREATORA (kod = UUID encji, nie slug modułu systemowego):
   * bez zapytań o pola modułu systemowego i bez zakładki Automatyzacje.
   */
  creatorKartoteka?: boolean;
  /**
   * Wymuszenie KONKRETNEGO layoutu listy (akordeon kreatora menu tenanta,
   * 2026-07-03): tableId dostaje sufiks `::layout-<id>` — useTableLayout we
   * wszystkich panelach edytuje wskazany layout zamiast domyślnego.
   * Tylko scope 'module'/'system' (template ma jeden widok per slug).
   */
  forcedLayoutId?: string;
} = {}): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Zewnętrzny kontekst (gdy builder osadzony do edycji nazwanego widoku listy kartoteki):
  // system-scope zakładka „Kolumny" tworzy własny provider, więc przekazujemy do niego
  // interceptor/seed z zewnątrz — inaczej byłby przesłonięty. Brak = normalne użycie.
  const outerReferenceCtx = useReferenceListContext();
  const {
    slug: slugParam,
    entitySlug: entitySlugParam,
    viewSlug: viewSlugParam,
  } = useParams<{
    slug: string;
    entitySlug: string;
    viewSlug: string;
  }>();

  // Prop-driven z fallbackiem na useParams (prop-refaktor — blokada osadzenia rozwiązana).
  // Trasowe callery (TemplateListBuilderPage) nie przekazują propsów → useParams jak dotąd.
  const slug = codeProp ?? slugParam;
  const entitySlug = entitySlugProp ?? entitySlugParam;
  const viewSlug = viewSlugProp ?? viewSlugParam;
  const [selectedTab, setSelectedTab] = useState<ListBuilderTab>('columns');
  const [searchParams, setSearchParams] = useSearchParams();

  // Edytowany widok listy z adresu (`?layout=<id>`) — standalone kreator KAŻDEJ kartoteki
  // tenanta (systemowej i modułu); osadzenia (akordeon menu, panel ITE) sterują widokiem
  // propem `forcedLayoutId`, szablon ITE ma jeden widok per slug.
  const viewPickerEnabled =
    scope !== 'template' && !embeddedMode && !creatorKartoteka && !readOnlyMode && !forcedLayoutId;
  const layoutParam = viewPickerEnabled ? searchParams.get('layout') : null;
  const effectiveForcedLayoutId = forcedLayoutId ?? layoutParam ?? undefined;
  const selectView = useCallback(
    (layoutId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (layoutId) next.set('layout', layoutId);
          else next.delete('layout');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Moduł własny: zamień slug → id, żeby panele trafiły w ten sam layout
  // (`module-<id>` → /modules/:id/layouts) co strona rekordów.
  const moduleQuery = useQuery({
    queryKey: ['module-by-slug', slug],
    queryFn: async () => {
      const res = await api.get<{ module: { id: string; name: string } }>(`/modules/${slug}`);
      return res.data.module;
    },
    enabled: scope === 'module' && !!slug,
  });

  // Pola modułu systemowego (do pickerów buildera automatyzacji, scope='system').
  const systemFieldsQuery = useQuery({
    queryKey: ['system-module-fields', slug],
    queryFn: async () => {
      const res = await api.get<{
        fields: SystemModuleFieldDef[];
      }>(`/system-modules/${slug}/fields`);
      return res.data.fields ?? [];
    },
    enabled: scope === 'system' && !!slug && !creatorKartoteka,
  });

  // Paleta „Dodaj kolumnę" zakładki Kolumny (kartoteka systemowa, np. Kontrahenci) —
  // katalog pól tego samego modułu (core + ext), id=slug/name=nazwa pola. Bez tego
  // nowo dodane pola dodatkowe (ext) nigdy nie pojawiały się w Kreatorze Listy, bo
  // `ListColumnsPanel` renderuje sekcję „Dodaj kolumnę" tylko gdy `ReferenceListContext`
  // dostarcza `availableFields` (dotąd wpięte tylko dla trybu template/menu-embed).
  // Ten sam katalog jest źródłem NAZW pól (`useColumnLabel`) w pozostałych zakładkach
  // z listą kolumn (Widgety / Szybkie filtry / Kanban / Kalendarz) oraz TYPU i
  // filtrowalności pola (`fieldType`, `isFilterable`) — z nich zakładka Kalendarz bierze
  // pola daty (jako pole startu tylko takie, po których serwer umie filtrować zakres).
  // `mergeComputedProcessFields` dokleja pola WYLICZANE procesu (aktywny proces / etap)
  // z rejestru w kodzie — NIE z katalogu bazy, żeby nie wyciekały do Kreatora Procesów
  // i Formularza (ADR-0093, patrz `lib/processes/computed-fields.ts`).
  const systemAvailableFields = useMemo(() => {
    // Gotowość katalogu: `undefined` DOPÓKI zapytanie nie ma potwierdzonych danych —
    // nie `[]` z domyślnego fallbacku. `[]` jest truthy, więc soft-heal w ListColumnsPanel
    // (`allowedColumnIds = availableFields ? ... : undefined`) traktowałby stan ładowania
    // jak „katalog potwierdzony pusty" i przy zapisie w trakcie ładowania wyciąłby WSZYSTKIE
    // kolumny (core+ext) z zapisanego list_config.
    if (!systemFieldsQuery.data) return undefined;
    // Brama `isFieldListable` — ta sama reguła co w buildCoreColumns/buildExtColumns:
    // paleta oferuje TYLKO pola, które lista umie narysować (showInList + typ
    // renderowalny). Bez niej paleta oferowała pole, po którego dodaniu na liście
    // nie pojawiało się nic (showInList=false lub typ bez renderera).
    // `overrideSlugs`: pola-referencje rysowane nadpisaniem STRONY (np. vendor_id →
    // kompozytowa „Dostawca") muszą zostać w palecie, inaczej ich usunięcie z układu
    // byłoby nieodwracalne z UI. Bramka `scope === 'system'` z tego samego powodu co
    // przy polach procesu niżej: moduły własne/szablony mają osobny slug-space (§4.10)
    // — slug modułu własnego pokrywający się z fakturami nie może wciągnąć mapy.
    const overrideSlugs = new Set(
      scope === 'system' ? (PAGE_OVERRIDE_REFERENCE_SLUGS[slug ?? ''] ?? []) : [],
    );
    const catalog: ReferenceListAvailableField[] = systemFieldsQuery.data
      .filter((f) => isFieldListable(f, { overrideSlugs }))
      .map((f) => ({
        id: f.slug,
        name: f.name,
        fieldType: f.fieldType,
        // Filtrowalność serwerowa (zakładka Kalendarz filtruje listę zakresem na polu startu):
        // pole dodatkowe (ext) — tylko z flagą `is_filterable` (`isExtFieldFilterEligible`,
        // apply-header-filters.ts); pole rdzenne — zawsze (silnik list wyprowadza filtr ze
        // wszystkich kolumn rdzennych z whitelisty, bez flagi: `deriveAutoHeaderFilterColumns`).
        isFilterable: f.isCore || f.isFilterable,
      }));
    // Pola wyliczane procesu dokładamy WYŁĄCZNIE w scope systemowym (kartoteki
    // systemowe, np. faktury). Moduły własne/szablony mają osobny slug-space (§4.10)
    // — jawna bramka `scope`, żeby slug modułu własnego pokrywający się z
    // `purchase_invoices`/`sale_invoices` nigdy nie wciągnął tych kolumn.
    return scope === 'system' ? mergeComputedProcessFields(catalog, slug, t) : catalog;
  }, [systemFieldsQuery.data, slug, t, scope]);

  // Kontekst z katalogiem pól dla zakładek scope='system', które pokazują kolumny układu
  // (Kolumny / Widgety / Szybkie filtry / Kanban / Kalendarz). Poza providerem
  // `useColumnLabel` nie miał skąd wziąć nazw pól i panele pokazywały surowe slugi
  // (np. `date_from`) — rejestr statyczny zna tylko garść kolumn spoza katalogu.
  //
  // Zewnętrzny kontekst (osadzenie do edycji nazwanego widoku listy kartoteki —
  // KartotekaViewListBuilder) jest przenoszony w całości (spread), żeby zagnieżdżenie niczego
  // nie zgubiło: `onListConfigSave` (interceptor zapisu) + `initialListConfig` (seed) włączają w
  // `useTableLayout` tryb nazwanego widoku, który po zapisie woła `onViewConfigPersisted`, a
  // `kartotekaFields`/`kartotekaDefaultListConfig` służą tam za seed. BEZ zewnętrznego
  // kontekstu (zwykłe użycie systemowe) domyślne `kartotekaFields=[]` i pusty
  // `onViewConfigPersisted` są nieużywane — brak interceptora zapisu = zwykła ścieżka zapisu
  // przez API, więc zachowanie paneli bez zmian. Nadpisujemy tylko katalog (zewnętrzny ma
  // pierwszeństwo nad katalogiem z zapytania) i `builtinColumnsAvailable` (kartoteka
  // systemowa: built-iny record_name/record_number nie są realnymi kolumnami).
  const effectiveCatalog = outerReferenceCtx?.availableFields ?? systemAvailableFields;
  const systemReferenceCtx = useMemo<ReferenceListContextValue>(
    () => ({
      kartotekaFields: [],
      onViewConfigPersisted: () => {},
      ...outerReferenceCtx,
      availableFields: effectiveCatalog,
      builtinColumnsAvailable: outerReferenceCtx?.builtinColumnsAvailable ?? false,
    }),
    [outerReferenceCtx, effectiveCatalog],
  );
  // Owija panel katalogiem pól TYLKO w scope='system' (moduły własne/szablony mają
  // osobny slug-space — §4.10 — i własne źródło etykiet; tam panel zostaje bez zmian).
  const withSystemCatalog = (panel: React.ReactElement): React.ReactElement =>
    scope === 'system' ? (
      <ReferenceListContext.Provider value={systemReferenceCtx}>
        {panel}
      </ReferenceListContext.Provider>
    ) : (
      panel
    );

  // Tryb template: szczegół szablonu tylko do nagłówka (panele mają własny fetch
  // przez useTableLayout — ten sam klucz cache, więc strzał jest jeden).
  const templateQuery = useModuleTemplateDetail(slug, { enabled: scope === 'template' });
  const templateEntity =
    scope === 'template'
      ? templateQuery.data?.entities.find((e) => e.slug === entitySlug)
      : undefined;

  const baseTableId =
    scope === 'template'
      ? slug && entitySlug && viewSlug
        ? templateTableId(slug, entitySlug, viewSlug)
        : null
      : scope === 'module'
        ? moduleQuery.data
          ? `module-${moduleQuery.data.id}`
          : null
        : (slug ?? null);
  // Sufiks wymuszonego layoutu (kreator menu tenanta) — patrz doc propa.
  const tableId =
    baseTableId && effectiveForcedLayoutId && scope !== 'template'
      ? `${baseTableId}::layout-${effectiveForcedLayoutId}`
      : baseTableId;
  // Edytowany widok tylko do odczytu (centralny / z menu modułu — serwer odrzuca zmianę treści):
  // panele w trybie podglądu + pasek „zrób kopię". Bez tego zapis kończyłby się cichym 403 przy
  // toaście „Zapisano" (useTableLayout łyka 403). Ten sam klucz cache co panele — bez dodatkowego
  // zapytania.
  const { layout: editedLayout, availableLayouts: listViews } = useTableLayout(tableId ?? '', {
    enabled: viewPickerEnabled && !!tableId,
  });
  const editedViewReadOnly =
    viewPickerEnabled && !!editedLayout && isReadOnlyListView(editedLayout);
  const panelsReadOnly = readOnlyMode || editedViewReadOnly;
  const { duplicate: duplicateView, isPending: duplicatingView } = useCreateListView({
    collection: tableId ? collectionEndpointFor(tableId) : '',
    availableLayouts: listViews,
    onSelect: selectView,
  });
  const headerName =
    scope === 'template'
      ? templateQuery.data && templateEntity
        ? `${templateQuery.data.name} · ${templateEntity.name}`
        : `${slug}/${entitySlug}`
      : scope === 'module'
        ? moduleQuery.data?.name
        : slug;
  // Karta kreatora kartoteki modułu: nazwa kartoteki zamiast etykiety ze ścieżki.
  useTabLabel(
    scope === 'module' && !embeddedMode && moduleQuery.data
      ? `${moduleQuery.data.name} · ${t('tenant.listBuilder.title')}`
      : null,
  );

  // Automatyzacje: aktywne tylko dla modułów systemowych (Faza B). Szablony (ADM,
  // brak tenantowego store) i moduły custom (osobny panel w ustawieniach) — tu zablokowane.
  const tabEnabled = (tab: TabDef): boolean =>
    tab.enabled || (tab.id === 'automations' && scope === 'system' && !creatorKartoteka);
  // Zakładki trybów widoku (Kanban / Kalendarz) znikają, gdy lista systemowa danego trybu
  // nie obsługuje (np. kontakty kontrahentów bez kalendarza) — ta sama deklaracja, z której
  // korzysta strona listy (`@/lib/list-view-support`). Moduły własne i szablony zostają bez zmian.
  const visibleTabs = TABS.filter(
    (tab) => scope !== 'system' || !tab.viewMode || listSupportsViewMode(slug, tab.viewMode),
  );
  // Zaznaczona zakładka, która znikła (np. zmiana sluga przy tym samym zamontowanym kreatorze),
  // nie renderuje panelu — wracamy do domyślnej „Kolumny".
  const activeTab: ListBuilderTab = visibleTabs.some((tab) => tab.id === selectedTab)
    ? selectedTab
    : 'columns';
  // Zapamiętany wybór też wraca do „Kolumny" (korekta stanu w trakcie renderu, wzorzec React):
  // inaczej powrót do listy z kalendarzem otwierałby ponownie zakładkę ukrytą na liście bez niego.
  if (activeTab !== selectedTab) setSelectedTab(activeTab);
  const activeTabDef = visibleTabs.find((tab) => tab.id === activeTab);
  const activeEnabled = activeTabDef ? tabEnabled(activeTabDef) : false;

  return (
    <div className={embeddedMode ? 'space-y-4' : 'p-6 space-y-4'} data-testid="list-builder-page">
      {!embeddedMode && (
        <header className="space-y-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-text-primary"
            data-testid="list-builder-back"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            {t('tenant.listBuilder.back')}
          </button>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-text-primary">
                {t('tenant.listBuilder.title')}
                {headerName ? (
                  <span className="ml-2 text-base font-normal text-text-tertiary">
                    · {headerName}
                  </span>
                ) : null}
              </h1>
              <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.subtitle')}</p>
            </div>
            {/* Wybór edytowanego widoku + nowy/duplikat (+ eksport/import dla kartotek
                systemowych) — każda kartoteka tenanta; szablony ITE mają jeden widok. */}
            {viewPickerEnabled && slug && tableId && (
              <ListViewSwitcher moduleSlug={slug} tableId={tableId} onSelect={selectView} />
            )}
          </div>
        </header>
      )}

      {editedViewReadOnly && editedLayout && (
        <Alert
          tone="info"
          title={t('tenant.listBuilder.views.readOnlyNotice.title', { name: editedLayout.name })}
          actions={
            <Button
              size="sm"
              variant="secondary"
              loading={duplicatingView}
              onClick={() => duplicateView(editedLayout)}
              data-testid="list-builder-readonly-duplicate"
            >
              {t('tenant.listBuilder.views.readOnlyNotice.duplicate')}
            </Button>
          }
          data-testid="list-builder-readonly-notice"
        >
          {t('tenant.listBuilder.views.readOnlyNotice.body')}
        </Alert>
      )}

      {/* Pasek zakładek (port z modala SellCall) */}
      <div
        role="tablist"
        className="flex flex-wrap gap-1 border-b border-border-secondary"
        data-testid="list-builder-tabs"
      >
        {visibleTabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const enabled = tabEnabled(tab);
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={!enabled}
              title={enabled ? undefined : t('tenant.listBuilder.soon')}
              onClick={() => enabled && setSelectedTab(tab.id)}
              data-testid={`list-builder-tab-${tab.id}`}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
                isActive
                  ? 'border-accent-600 text-accent-700 dark:text-accent-300'
                  : enabled
                    ? 'border-transparent text-text-secondary hover:text-text-primary'
                    : 'border-transparent text-text-muted cursor-not-allowed opacity-60'
              }`}
            >
              <tab.Icon size={14} aria-hidden="true" />
              {t(tab.labelKey)}
              {!enabled && (
                <span className="ml-1 rounded bg-bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-muted">
                  {t('tenant.listBuilder.soon')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Treść aktywnej zakładki */}
      <div
        role="tabpanel"
        className="rounded-lg border border-border-secondary bg-bg-elevated p-6"
        data-testid={`list-builder-panel-${activeTab}`}
        {...(panelsReadOnly
          ? {
              style: { pointerEvents: 'none', userSelect: 'none' },
              // inert: usuwa focusowalne kontrolki z tab order i blokuje klawiaturę
              // (React 19 wspiera inert jako natywny bool prop HTML). pointer-events:none
              // chroni mysz; inert chroni klawiaturę.
              inert: true,
            }
          : {})}
        aria-disabled={panelsReadOnly || undefined}
      >
        {scope === 'module' && moduleQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !tableId ? (
          <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>
        ) : (
          <>
            {/* Notka UX (hub): akcja „przejdź do" własnych przycisków przyjmuje ścieżki
                routera TENANTA — w szablonie projektant musi wpisywać je „w ciemno". */}
            {scope === 'template' && activeTab === 'buttons' && (
              <p
                className="mb-4 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                data-testid="list-builder-template-buttons-hint"
              >
                {t('moduleTemplateEditor.layoutDesigner.buttonsPortabilityHint')}
              </p>
            )}
            {activeTab === 'widgets' &&
              withSystemCatalog(<ListWidgetsPanel slug={tableId} hideAddWidget={hideAddWidget} />)}
            {activeTab === 'columns' && withSystemCatalog(<ListColumnsPanel slug={tableId} />)}
            {activeTab === 'quickFilters' &&
              withSystemCatalog(<ListQuickFiltersPanel slug={tableId} />)}
            {activeTab === 'page' && <ListPagePanel slug={tableId} />}
            {activeTab === 'buttons' && <ListButtonsPanel slug={tableId} />}
            {activeTab === 'actions' &&
              (() => {
                const lk = resolveListKartoteka(scope, slug);
                return (
                  <ListActionsPanel
                    slug={tableId}
                    kartotekaKey={lk.kartotekaKey}
                    documentClass={lk.documentClass}
                  />
                );
              })()}
            {activeTab === 'kanban' && withSystemCatalog(<ListKanbanPanel slug={tableId} />)}
            {activeTab === 'calendar' &&
              withSystemCatalog(
                // Pola kalendarza z katalogu pól (nie z kolumn układu), daty startu/końca po typie
                // — katalog z typami jest dostępny wyłącznie w scope='system'. Moduły własne i
                // szablony: typ pola nieznany w tym kontekście, listy zostają jak dotąd
                // (wszystkie kolumny układu) — patrz JSDoc panelu.
                // Błąd katalogu pokazujemy tylko, gdy NIE MA żadnego katalogu, a jego źródłem jest
                // własne zapytanie strony: bez zewnętrznego kontekstu (osadzenie z kontekstem —
                // np. KartotekaViewListBuilder w panelu ITE — ma własne źródło pól, a to
                // zapytanie może odpowiadać błędem niezależnie, więc „Spróbuj ponownie" odświeżałoby
                // złe zapytanie) oraz bez wcześniej pobranych danych (nieudane odświeżenie
                // zostawia dane w cache — katalog nadal jest).
                <ListCalendarPanel
                  slug={tableId}
                  dateFieldsOnly={scope === 'system'}
                  catalogError={
                    scope === 'system' &&
                    outerReferenceCtx === null &&
                    systemFieldsQuery.isError &&
                    systemFieldsQuery.data === undefined
                  }
                  onCatalogRetry={() => void systemFieldsQuery.refetch()}
                />,
              )}
            {activeTab === 'automations' && scope === 'system' && !creatorKartoteka && slug && (
              <AutomationsPanel
                basePath={`/system-modules/${slug}/automations`}
                fields={(systemFieldsQuery.data ?? []) as unknown as ModuleField[]}
              />
            )}
            {!activeEnabled && (
              <p className="text-sm text-text-muted">{t('tenant.listBuilder.soonHint')}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
